// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useRef, useState } from 'react'
import {
  Play, Square, AlertCircle, CheckCircle2, Loader2, FileText,
} from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'

/**
 * /admin/ops/carrier-order-import — ops surface for the list-scoped carrier-order
 * backfill (POST shipping/carrier-orders/backfill-from-list).
 *
 * Replaces a local shell script. The import itself already existed and is unchanged;
 * this is only a driver for it, so operators are not pasting bearer tokens into a
 * terminal to give a pharmacy its historical shipments back.
 *
 * WHY THE CSV IS PARSED IN THE BROWSER AND NEVER UPLOADED
 * The courier file is customer billing data. It is read with FileReader, split here,
 * and only bare order numbers are sent. The file never reaches a server and is never
 * persisted — so there is no new store of courier data to secure, retain or purge.
 *
 * WHY THE LINE-TYPE FILTER EXISTS
 * The courier report carries a Line Type column. Rows marked "Account / zero-charge
 * line" (INVOICE / RTS / PICKUP / NEXTDAYRA / NOCHARGE / SCHEDULED) are NOT shipments —
 * an INVOICE row is a monthly bill, an RTS is a return. The report itself states these
 * "never appear in the portal by design". Default is therefore deliveries only.
 * Including them is a deliberate, owner-level choice, so the toggle warns rather than
 * hiding the option.
 *
 * WHY IT STOPS ITSELF
 * The endpoint is insert-only and de-duplicates per order, so a re-run cannot create
 * duplicates. What it cannot detect is the carrier having aged orders off its API: that
 * shows up as a rising `unavailable` count, and continuing past it just burns time
 * writing nothing. The run halts on a sustained unavailable/error rate and reports why.
 *
 * PACING
 * Batches are deliberately small with a gap between them. A tenant DB on a serverless
 * tier has been taken down by sustained load before (2026-08-06); a bulk import is
 * exactly that shape of workload. Import speed is worth less than a pharmacy staying up.
 *
 * Permission: shipping.backfill (the backend also requires admin — the UI gate mirrors,
 * never replaces, the server check).
 */

type Totals = Omit<api.CarrierOrderBackfillResult, 'error_samples' | 'dry_run'>

const ZERO: Totals = {
  processed: 0, inserted: 0, skipped_existing: 0,
  unavailable: 0, would_insert: 0, errors: 0,
}

// Halt thresholds. Only applied once enough orders have been seen that the rate is
// meaningful — an early 1-of-50 blip should not stop a 9,000-order run.
const MIN_SAMPLE_BEFORE_HALT = 200
const UNAVAILABLE_HALT_RATE = 0.2
const ERROR_HALT_RATE = 0.1

const BATCH_SIZE = 50
const BATCH_GAP_MS = 5000

const ZERO_CHARGE_LABEL = 'Account / zero-charge line'

type ParsedCsv = {
  fileName: string
  delivery: string[]
  zeroCharge: string[]
  skipped: number       // rows whose order number was not a plain number
}

/**
 * Split the courier CSV. Tolerant by design: the header block above the column row
 * varies between exports, so the column row is located by name rather than assumed to
 * be at a fixed offset. A row whose order number is not numeric is counted, not
 * silently dropped — a parse that quietly loses rows is worse than one that says so.
 */
function parseCsv(text: string, fileName: string): ParsedCsv {
  const lines = text.split(/\r?\n/)
  const headerIdx = lines.findIndex(l => /(^|,)\s*"?Order"?\s*(,|$)/i.test(l) && /Line Type/i.test(l))
  if (headerIdx < 0) throw new Error('Could not find the column header row (expected "Order" and "Line Type").')

  const cols = splitCsvLine(lines[headerIdx]).map(c => c.trim().toLowerCase())
  const orderCol = cols.indexOf('order')
  const typeCol = cols.indexOf('line type')
  if (orderCol < 0 || typeCol < 0) throw new Error('The header row is missing an "Order" or "Line Type" column.')

  const delivery: string[] = []
  const zeroCharge: string[] = []
  const seen = new Set<string>()
  let skipped = 0

  for (let i = headerIdx + 1; i < lines.length; i++) {
    const raw = lines[i]
    if (!raw.trim()) continue
    const cells = splitCsvLine(raw)
    const order = (cells[orderCol] ?? '').trim()
    if (!/^\d+$/.test(order)) { skipped++; continue }
    if (seen.has(order)) continue
    seen.add(order)
    const lineType = (cells[typeCol] ?? '').trim()
    if (lineType.toLowerCase() === ZERO_CHARGE_LABEL.toLowerCase()) zeroCharge.push(order)
    else delivery.push(order)
  }

  return { fileName, delivery, zeroCharge, skipped }
}

/** Minimal CSV field splitter: handles quoted fields containing commas. */
function splitCsvLine(line: string): string[] {
  const out: string[] = []
  let cur = ''
  let inQ = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQ) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++ } else inQ = false
      } else cur += ch
    } else if (ch === '"') inQ = true
    else if (ch === ',') { out.push(cur); cur = '' }
    else cur += ch
  }
  out.push(cur)
  return out
}

export default function CarrierOrderImportPage() {
  const fileRef = useRef<HTMLInputElement>(null)
  const stopRef = useRef(false)

  const [parsed, setParsed] = useState<ParsedCsv | null>(null)
  const [parseError, setParseError] = useState<string | null>(null)
  const [accountId, setAccountId] = useState('')
  const [includeZeroCharge, setIncludeZeroCharge] = useState(false)
  const [dryRun, setDryRun] = useState(true)

  const [running, setRunning] = useState(false)
  const [totals, setTotals] = useState<Totals>(ZERO)
  const [sent, setSent] = useState(0)
  const [target, setTarget] = useState(0)
  const [log, setLog] = useState<string[]>([])
  const [haltReason, setHaltReason] = useState<string | null>(null)
  const [runError, setRunError] = useState<string | null>(null)

  const orders = parsed
    ? (includeZeroCharge ? [...parsed.delivery, ...parsed.zeroCharge] : parsed.delivery)
    : []

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (!f) return
    setParseError(null); setParsed(null)
    setTotals(ZERO); setSent(0); setLog([]); setHaltReason(null); setRunError(null)
    const reader = new FileReader()
    reader.onerror = () => setParseError('Could not read the file.')
    reader.onload = () => {
      try { setParsed(parseCsv(String(reader.result ?? ''), f.name)) }
      catch (err: any) { setParseError(err?.message ?? 'Could not parse the file.') }
    }
    reader.readAsText(f)
  }

  async function run() {
    if (!orders.length || !accountId.trim()) return
    stopRef.current = false
    setRunning(true)
    setTotals(ZERO); setSent(0); setLog([]); setHaltReason(null); setRunError(null)
    setTarget(orders.length)

    const acc = ZERO
    let agg: Totals = { ...acc }
    let done = 0

    try {
      for (let i = 0; i < orders.length; i += BATCH_SIZE) {
        if (stopRef.current) { setHaltReason('Stopped by operator.'); break }
        const slice = orders.slice(i, i + BATCH_SIZE)

        const res = await api.backfillCarrierOrdersFromList({
          carrier_account_id: accountId.trim(), orders: slice, dry_run: dryRun,
        })

        agg = {
          processed: agg.processed + (res.processed ?? 0),
          inserted: agg.inserted + (res.inserted ?? 0),
          skipped_existing: agg.skipped_existing + (res.skipped_existing ?? 0),
          unavailable: agg.unavailable + (res.unavailable ?? 0),
          would_insert: agg.would_insert + (res.would_insert ?? 0),
          errors: agg.errors + (res.errors ?? 0),
        }
        done += slice.length
        setTotals(agg); setSent(done)

        const sample = res.error_samples?.length ? ` — ${res.error_samples[0]}` : ''
        setLog(l => [
          `batch ${Math.floor(i / BATCH_SIZE) + 1}: +${res.inserted ?? 0} inserted, ` +
          `${res.skipped_existing ?? 0} already present, ${res.unavailable ?? 0} unavailable, ` +
          `${res.errors ?? 0} errors${sample}`,
          ...l,
        ].slice(0, 200))

        // Self-halt: a carrier that no longer serves orders this old, or a batch failing
        // systematically, should not be allowed to chew through the whole list unattended.
        if (agg.processed >= MIN_SAMPLE_BEFORE_HALT) {
          const uRate = agg.unavailable / agg.processed
          const eRate = agg.errors / agg.processed
          if (uRate >= UNAVAILABLE_HALT_RATE) {
            setHaltReason(
              `Stopped: ${Math.round(uRate * 100)}% of orders came back unavailable. ` +
              `The carrier may no longer hold orders this old.`)
            break
          }
          if (eRate >= ERROR_HALT_RATE) {
            setHaltReason(`Stopped: ${Math.round(eRate * 100)}% of orders returned an error.`)
            break
          }
        }

        if (i + BATCH_SIZE < orders.length && !stopRef.current) {
          await new Promise(r => setTimeout(r, BATCH_GAP_MS))
        }
      }
    } catch (err: any) {
      setRunError(friendlyError(err))
    } finally {
      setRunning(false)
    }
  }

  const pct = target ? Math.round((sent / target) * 100) : 0

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-center gap-2 mb-1">
        <FileText size={20} />
        <h1 className="text-xl font-semibold">Carrier order import</h1>
      </div>
      <p className="text-sm text-gray-600 mb-6">
        Imports historical carrier orders that never reached the portal, so they appear in the
        shipment list. Orders already present are skipped, so re-running is safe. Imported rows
        are carrier records only — they carry no prescription or patient link.
      </p>

      {/* ── 1. File ─────────────────────────────────────────────── */}
      <section className="border rounded-lg p-4 mb-4">
        <h2 className="font-medium mb-2">1. Courier file</h2>
        <input
          ref={fileRef} type="file" accept=".csv,text/csv"
          onChange={onPickFile} disabled={running}
          className="block text-sm"
        />
        <p className="text-xs text-gray-500 mt-2">
          Read in your browser only — the file is never uploaded or stored. Just the order
          numbers are sent.
        </p>

        {parseError && (
          <div className="mt-3 flex items-start gap-2 text-sm text-red-700">
            <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{parseError}</span>
          </div>
        )}

        {parsed && (
          <div className="mt-3 text-sm">
            <div className="font-medium">{parsed.fileName}</div>
            <table className="mt-2">
              <tbody>
                <tr>
                  <td className="pr-6 py-0.5">Deliveries</td>
                  <td className="text-right tabular-nums">{parsed.delivery.length.toLocaleString()}</td>
                </tr>
                <tr className="text-gray-600">
                  <td className="pr-6 py-0.5">Account / zero-charge lines</td>
                  <td className="text-right tabular-nums">{parsed.zeroCharge.length.toLocaleString()}</td>
                </tr>
                {parsed.skipped > 0 && (
                  <tr className="text-amber-700">
                    <td className="pr-6 py-0.5">Rows without a numeric order</td>
                    <td className="text-right tabular-nums">{parsed.skipped.toLocaleString()}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── 2. Options ──────────────────────────────────────────── */}
      <section className="border rounded-lg p-4 mb-4">
        <h2 className="font-medium mb-3">2. What to send</h2>

        <label className="block text-sm mb-3">
          <span className="block mb-1">Carrier account ID</span>
          <input
            value={accountId} onChange={e => setAccountId(e.target.value)}
            disabled={running} placeholder="00000000-0000-0000-0000-000000000000"
            className="border rounded px-2 py-1 w-full max-w-md font-mono text-xs"
          />
          <span className="block text-xs text-gray-500 mt-1">
            The account must belong to this pharmacy; the server rejects it otherwise.
          </span>
        </label>

        <label className="flex items-start gap-2 text-sm mb-2">
          <input
            type="checkbox" checked={dryRun} disabled={running}
            onChange={e => setDryRun(e.target.checked)} className="mt-1"
          />
          <span>
            <span className="font-medium">Dry run</span> — check what would be imported without
            writing anything. Makes no carrier calls, so it cannot tell you whether the carrier
            still holds these orders.
          </span>
        </label>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox" checked={includeZeroCharge} disabled={running}
            onChange={e => setIncludeZeroCharge(e.target.checked)} className="mt-1"
          />
          <span>
            Include account / zero-charge lines
            {parsed && parsed.zeroCharge.length > 0 && (
              <span className="text-amber-700">
                {' '}— adds {parsed.zeroCharge.length.toLocaleString()} rows that are invoices,
                returns and pickups rather than shipments. The courier report marks these as
                never appearing in the portal by design.
              </span>
            )}
          </span>
        </label>
      </section>

      {/* ── 3. Run ──────────────────────────────────────────────── */}
      <section className="border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-medium">
            3. Import{orders.length > 0 && <> — {orders.length.toLocaleString()} orders</>}
          </h2>
          <div className="flex gap-2">
            {!running ? (
              <button
                onClick={run}
                disabled={!orders.length || !accountId.trim()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-blue-600 text-white text-sm disabled:opacity-40"
              >
                <Play size={15} />{dryRun ? 'Start dry run' : 'Start import'}
              </button>
            ) : (
              <button
                onClick={() => { stopRef.current = true }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-red-600 text-white text-sm"
              >
                <Square size={15} />Stop
              </button>
            )}
          </div>
        </div>

        {!dryRun && !running && orders.length > 0 && (
          <div className="mb-3 flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>
              This writes {orders.length.toLocaleString()} shipment rows. There is no bulk undo —
              consider a dry run first, and starting small.
            </span>
          </div>
        )}

        {(running || sent > 0) && (
          <>
            <div className="flex items-center gap-2 mb-2 text-sm">
              {running && <Loader2 size={15} className="animate-spin" />}
              <span className="tabular-nums">
                {sent.toLocaleString()} / {target.toLocaleString()} ({pct}%)
              </span>
            </div>
            <div className="h-2 bg-gray-200 rounded overflow-hidden mb-3">
              <div className="h-full bg-blue-600 transition-all" style={{ width: `${pct}%` }} />
            </div>

            <table className="text-sm mb-3">
              <tbody>
                <tr><td className="pr-6 py-0.5">Processed</td><td className="text-right tabular-nums">{totals.processed.toLocaleString()}</td></tr>
                <tr><td className="pr-6 py-0.5">{dryRun ? 'Would insert' : 'Inserted'}</td><td className="text-right tabular-nums font-medium">{(dryRun ? totals.would_insert : totals.inserted).toLocaleString()}</td></tr>
                <tr><td className="pr-6 py-0.5">Already present</td><td className="text-right tabular-nums">{totals.skipped_existing.toLocaleString()}</td></tr>
                <tr className={totals.unavailable > 0 ? 'text-amber-700' : undefined}>
                  <td className="pr-6 py-0.5">Unavailable at carrier</td><td className="text-right tabular-nums">{totals.unavailable.toLocaleString()}</td>
                </tr>
                <tr className={totals.errors > 0 ? 'text-red-700' : undefined}>
                  <td className="pr-6 py-0.5">Errors</td><td className="text-right tabular-nums">{totals.errors.toLocaleString()}</td>
                </tr>
              </tbody>
            </table>
          </>
        )}

        {haltReason && (
          <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
            <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{haltReason}</span>
          </div>
        )}
        {runError && (
          <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-3">
            <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{runError}</span>
          </div>
        )}
        {!running && sent > 0 && !haltReason && !runError && sent >= target && (
          <div className="flex items-start gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded p-2 mb-3">
            <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
            <span>Finished{dryRun ? ' (dry run — nothing was written).' : '.'}</span>
          </div>
        )}

        {log.length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-gray-600">Batch log</summary>
            <div className="mt-2 font-mono space-y-0.5 max-h-64 overflow-auto">
              {log.map((l, i) => <div key={i}>{l}</div>)}
            </div>
          </details>
        )}
      </section>
    </div>
  )
}
